import { Form } from 'semantic';
import React from 'react';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { useTranslation } from 'react-i18next';
import {
  DropdownOption,
  TariffComponent,
  PriceType,
  PriceTypeValues,
  TimePrice,
} from 'screens/GroupsAndPolicies/types';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import PriceField from 'components/form-fields/formik/PriceField';
import { FormikProps } from 'formik';
import InputField from 'components/form-fields/formik/InputField';
import { TimeFormatField } from 'screens/GroupsAndPolicies/Fields/TimeFormatField';
import { buildFormPath } from 'screens/GroupsAndPolicies/formpath';

type Props = {
  values: FormikProps<TariffComponent['price']>['values'];
  basePath?: string;
};

export const PriceSelectionFormFields = (props: Props) => {
  const { t } = useTranslation();

  return (
    <Form.Group class="">
      <DropdownField
        name={buildFormPath('price.type', props.basePath)}
        selection
        required
        label={t('tariff.priceSelection.priceType', 'Price type')}
        options={getPriceOptions()}
      />

      {props.values?.type === 'TIME' && (
        <>
          <TimeFormatField
            name={buildFormPath('price.format', props.basePath)}
          />
          <PriceField
            name={buildFormPath('price.value', props.basePath)}
            required
            label={t(
              'tariff.priceSelection.pricePerTimeUnit',
              'Price per hour'
            )}
          />
          <InputField
            name={buildFormPath('price.stepSize', props.basePath)}
            min={1}
            max={3600}
            label={t('tariff.priceSelection.stepSize', 'Step size')}
            type="number"
          />
        </>
      )}

      {props.values?.type === 'ENERGY' && (
        <>
          <PriceField
            name={buildFormPath('price.value', props.basePath)}
            label={t('tariff.priceSelection.pricePerKwh', 'Price per kWh')}
          />
          <InputField
            name={buildFormPath('price.stepSize', props.basePath)}
            min={1}
            max={3600}
            required
            label={t('tariff.priceSelection.stepSize', 'Step size')}
            type="number"
          />
        </>
      )}

      {props.values?.type === 'FLAT' && (
        <PriceField
          name={buildFormPath('price.value', props.basePath)}
          label={t(
            'tariff.priceSelection.pricePerSession',
            'Price per session'
          )}
        />
      )}

      {props.values?.type === 'PARKING_TIME' && (
        <>
          <TimeFormatField
            name={buildFormPath('price.format', props.basePath)}
          />
          <PriceField
            name={buildFormPath('price.value', props.basePath)}
            label={t(
              'tariff.priceSelection.pricePerParkedUnit',
              'Price per parked {{unit}}',
              { unit: (props.values as TimePrice)?.format } // TODO: these also need translations
            )}
          />
          <InputField
            name={buildFormPath('price.stepSize', props.basePath)}
            min={1}
            max={3600}
            required
            label={t('tariff.priceSelection.stepSize', 'Step size')}
            type="number"
          />
        </>
      )}

      {props.values?.type === 'DYNAMIC' && (
        <>
          <PriceField
            name={buildFormPath('price.surcharge', props.basePath)}
            label={t('tariff.priceSelection.surcharge', 'Surcharge')}
          />

          <PriceField
            name={buildFormPath('price.backup', props.basePath)}
            label={t('tariff.priceSelection.backupTariff', 'Backup tariff')}
          />
        </>
      )}

      {/* TODO: feels a bit wrong to have this here */}
      <CheckboxField
        style={{ marginTop: '2.4rem' }}
        name={buildFormPath('restrictions.enabled', props.basePath)}
        label={t(
          'tariff.priceSelection.applyTariffRestrictions',
          'Apply tariff restrictions'
        )}
      />
    </Form.Group>
  );
};

const getPriceOptions = (): DropdownOption<PriceType>[] => {
  const { t } = useTranslation();

  return PriceTypeValues.map((value) => ({
    key: value,
    value,
    text: t(`tariff.priceSelection.${value.toLowerCase()}`, value),
  }));
};
